import React from 'react';
import { SingleDatepicker } from 'chakra-dayzed-datepicker';
import { formatDate } from '@/shared/parse';
import moment from 'moment';

const datepickerConfig = {
  inputProps: {
    size: 'md',
    width: '100%',
    minWidth: '200px',
    paddingRight: '40px'
  },
  popoverCompProps: {
    popoverContentProps: {
      zIndex: 10
    }
  },
  dateNavBtnProps: {
    colorScheme: 'brand'
  },
  dayOfMonthBtnProps: {
    defaultBtnProps: {
      _hover: {
        background: 'brand.100'
      }
    },
    isInRangeBtnProps: {
      color: 'brand.200'
    },
    selectedBtnProps: {
      background: 'brand.100',
      color: 'brand.600'
    },
    todayBtnProps: {
      background: 'brand.50'
    }
  }
};

export interface ClearableDatePickerProps {
  date: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  minDate?: Date;
  [key: string]: any;
}

export const DeadlineDatePicker: React.FC<ClearableDatePickerProps> = ({ 
  date, 
  onDateChange, 
  minDate, 
  ...props 
}) => {
  const [inputValue, setInputValue] = React.useState('');
  
  // Update input value when date changes
  React.useEffect(() => {
    if (date) {
      setInputValue(formatDate(date));
    } else {
      setInputValue('');
    }
  }, [date]);

  // Parse date using moment with the exact format specified in component config
  const parseDate = (dateString: string): Date | null => {
    const trimmed = dateString.trim();
    if (!trimmed) return null;
    
    // Use exact format: 'dd/MM/yyyy' -> 'DD/MM/YYYY' in moment
    const parsed = moment(trimmed, 'DD/MM/YYYY', true);
    return parsed.isValid() ? parsed.toDate() : null;
  };

  return (
    <SingleDatepicker
      triggerVariant='input'
      date={date}
      onDateChange={(earliestDeadline) => {

      const endOfTheDay = new Date(earliestDeadline.getFullYear(), earliestDeadline.getMonth(), earliestDeadline.getDate(), 23, 59, 59, 0);
        onDateChange(endOfTheDay);
        if (endOfTheDay) {
          setInputValue(formatDate(endOfTheDay));
        } else {
          setInputValue('');
        }
      }}
      configs={{
        dateFormat: 'dd/MM/yyyy',
        firstDayOfWeek: 1, // Monday
      }}
      minDate={minDate}
      propsConfigs={{
        ...datepickerConfig,
        inputProps: {
          ...datepickerConfig.inputProps,
          value: inputValue,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setInputValue(value);
            
            if (value === '') {
              onDateChange(undefined);
            } else {
              // Try to parse the manually entered date
              const parsedDate = parseDate(value);
              if (parsedDate) {
                // Convert to end of day like the date picker does
                const endOfTheDay = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate(), 23, 59, 59, 0);
                onDateChange(endOfTheDay);
              }
            }
          }
        }
      }}
      {...props}
    />
  );
};
